import React from 'react';
import toast from 'react-hot-toast';
import { Copy } from 'lucide-react';

export interface FieldDef {
  label: string;
  value: React.ReactNode;
  /** Show a copy-to-clipboard button (for system IDs). Copies `copyValue`. */
  copyValue?: string;
  /** Span both columns for prose or legal text. */
  fullWidth?: boolean;
  /** Long prose wraps at words; identifiers retain the legacy character break. */
  wrap?: 'words' | 'characters';
}

/**
 * Read-only field grid for detail Overview tabs. Per
 * ACCOUNTS_TOOL_REQUIREMENTS.md §3.0, every DB field — including system IDs
 * and timestamps — is visible here; system fields are display-only and IDs
 * get copy-to-clipboard.
 */
const FieldList: React.FC<{ fields: FieldDef[] }> = ({ fields }) => {
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {fields.map((field) => (
        <div key={field.label} className={`min-w-0 ${field.fullWidth ? 'sm:col-span-2' : ''}`}>
          <dt className="text-xs font-medium uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
            {field.label}
          </dt>
          <dd className={`mt-0.5 flex items-center gap-1.5 text-sm text-text-primary dark:text-text-dark-primary ${field.wrap === 'words' ? 'break-words' : 'break-all'}`}>
            <span className="min-w-0">{field.value ?? '—'}</span>
            {field.copyValue && (
              <button
                onClick={() => copy(field.copyValue!)}
                aria-label={`Copy ${field.label}`}
                className="shrink-0 rounded p-0.5 text-text-tertiary hover:bg-background-tertiary hover:text-text-primary dark:text-text-dark-tertiary dark:hover:bg-background-dark-tertiary dark:hover:text-text-dark-primary"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export default FieldList;
